// Import Actuals — bulk-load recorded customer actuals from CSV (paste or file).
// Columns: Customer (name or code), Hub (name or code), Date (MM-DD-YYYY or
// YYYY-MM-DD), Volume. Rows are matched to customers/hubs, validated, previewed,
// then upserted into customer_actuals. Also offers a one-click clear-all.

import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../auth/AuthProvider";
import { Button, Card, Textarea } from "../../components/ui";
import { getErrorMessage } from "../../lib/errors";
import { fmtDate, fmtN } from "../../lib/format";
import type { Customer, Hub } from "../../lib/types";
import { listCustomers } from "../customers/customersApi";
import { listHubs } from "../hubs/hubsApi";
import {
  deleteAllCustomerActuals,
  saveCustomerActuals,
  type CustomerActual,
} from "./customerActualsApi";

interface ParsedRow {
  line: number;
  customer: string;
  hub: string;
  date: string;
  customerId?: string;
  hubId?: string;
  isoDate?: string;
  cartons?: number;
  polybags?: number;
  irregular?: number;
  vol?: number; // total
  hasBreakdown: boolean;
  error?: string;
}

const TEMPLATE =
  "Customer,Hub,Date,Cartons,Polybags,Irregular\n" +
  "ACME,NYSC,06-15-2026,700,200,100\n" +
  "NKE,ATSC,06-15-2026,1500,750,250";

const num = (s: string) => Number((s ?? "").replace(/[, ]/g, ""));

/** Normalize a date cell to YYYY-MM-DD (accepts YYYY-MM-DD or M-D-Y / M/D/Y). */
function normalizeDate(s: string): string | null {
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/); // M/D/Y or M-D-Y
  if (m) return `${m[3]}-${m[1].padStart(2, "0")}-${m[2].padStart(2, "0")}`;
  return null;
}

export default function ImportActualsPage() {
  const { activeAccount } = useAuth();
  const accountId = activeAccount?.accountId ?? null;

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [hubs, setHubs] = useState<Hub[]>([]);
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState<ParsedRow[] | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!accountId) return;
    Promise.all([listCustomers(accountId), listHubs(accountId)])
      .then(([c, h]) => {
        setCustomers(c);
        setHubs(h);
      })
      .catch((e) => setError(getErrorMessage(e, "Failed to load customers/hubs.")));
  }, [accountId]);

  // name/code → id lookups (case-insensitive).
  const custLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of customers) {
      m.set(c.name.toLowerCase(), c.id);
      if (c.code) m.set(c.code.toLowerCase(), c.id);
    }
    return m;
  }, [customers]);
  const hubLookup = useMemo(() => {
    const m = new Map<string, string>();
    for (const h of hubs) {
      m.set(h.name.toLowerCase(), h.id);
      if (h.code) m.set(h.code.toLowerCase(), h.id);
    }
    return m;
  }, [hubs]);

  function parse() {
    setMsg(null);
    setError(null);
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter((l) => l !== "");
    const out: ParsedRow[] = [];
    lines.forEach((raw, i) => {
      const cells = raw.split(",").map((c) => c.trim());
      // Skip a header row (first row whose first numeric column isn't a number).
      if (i === 0 && cells[3] !== undefined && Number.isNaN(num(cells[3]))) return;

      const [customer = "", hub = "", date = ""] = cells;
      const hasBreakdown = cells.length >= 6; // Cartons,Polybags,Irregular present
      const row: ParsedRow = { line: i + 1, customer, hub, date, hasBreakdown };
      row.customerId = custLookup.get(customer.toLowerCase());
      row.hubId = hubLookup.get(hub.toLowerCase());
      row.isoDate = normalizeDate(date) ?? undefined;

      if (hasBreakdown) {
        const c = num(cells[3]);
        const p = num(cells[4]);
        const ir = num(cells[5]);
        if ([c, p, ir].every((n) => Number.isFinite(n) && n >= 0)) {
          row.cartons = Math.round(c);
          row.polybags = Math.round(p);
          row.irregular = Math.round(ir);
          row.vol = row.cartons + row.polybags + row.irregular;
        }
      } else {
        const v = num(cells[3]);
        if (Number.isFinite(v) && v >= 0) row.vol = Math.round(v);
      }

      if (!row.customerId) row.error = `Unknown customer "${customer}"`;
      else if (!row.hubId) row.error = `Unknown hub "${hub}"`;
      else if (!row.isoDate) row.error = `Bad date "${date}"`;
      else if (row.vol == null) row.error = "Bad / missing volume";
      out.push(row);
    });
    setParsed(out);
  }

  const valid = useMemo(() => (parsed ?? []).filter((r) => !r.error), [parsed]);
  const invalid = useMemo(() => (parsed ?? []).filter((r) => r.error), [parsed]);

  async function importRows() {
    if (!accountId || valid.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const entries: CustomerActual[] = valid.map((r) => ({
        customerId: r.customerId!,
        hubId: r.hubId!,
        date: r.isoDate!,
        actualVolume: r.vol!,
        cartons: r.hasBreakdown ? (r.cartons ?? null) : null,
        polybags: r.hasBreakdown ? (r.polybags ?? null) : null,
        irregular: r.hasBreakdown ? (r.irregular ?? null) : null,
      }));
      await saveCustomerActuals(accountId, entries);
      setMsg(`Imported ${entries.length} actual${entries.length === 1 ? "" : "s"}.`);
      setParsed(null);
      setText("");
    } catch (e) {
      setError(getErrorMessage(e, "Import failed."));
    } finally {
      setBusy(false);
    }
  }

  async function clearAll() {
    if (!accountId) return;
    if (!confirm("Delete ALL customer actuals for this account? This cannot be undone.")) return;
    setBusy(true);
    setError(null);
    try {
      await deleteAllCustomerActuals(accountId);
      setMsg("All customer actuals deleted.");
    } catch (e) {
      setError(getErrorMessage(e, "Delete failed."));
    } finally {
      setBusy(false);
    }
  }

  function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result ?? ""));
    reader.readAsText(file);
  }

  if (!accountId) {
    return (
      <Card>
        <p className="text-sm text-slate-500">No active account. Try signing out and back in.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold tracking-tight text-slate-900">Import Actuals</h1>
        {msg && <span className="text-sm text-emerald-600">{msg}</span>}
      </div>
      <p className="text-xs text-slate-500">
        Paste or upload CSV:{" "}
        <span className="font-medium">Customer, Hub, Date, Cartons, Polybags, Irregular</span> (total =
        their sum). A 4-column <span className="font-medium">Customer, Hub, Date, Volume</span> form
        also works (no type split). Customer/Hub match by name or code; Date is MM-DD-YYYY or
        YYYY-MM-DD. Existing rows for the same customer/hub/date are overwritten.
      </p>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <Card className="space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv,text/plain"
            onChange={onFile}
            className="hidden"
          />
          <Button variant="ghost" type="button" onClick={() => fileRef.current?.click()}>
            Upload CSV
          </Button>
          {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
          <span className="text-xs text-slate-400">or paste below ·</span>
          <button
            type="button"
            onClick={() => setText(TEMPLATE)}
            className="text-xs font-medium text-brand-600 hover:underline"
          >
            Insert template
          </button>
        </div>
        <Textarea
          rows={8}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={TEMPLATE}
          className="font-mono text-xs"
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button onClick={parse} disabled={!text.trim()}>
              Parse
            </Button>
            {valid.length > 0 && (
              <Button onClick={importRows} disabled={busy}>
                {busy ? "…" : `Import ${valid.length} valid row${valid.length === 1 ? "" : "s"}`}
              </Button>
            )}
          </div>
          <Button variant="danger" onClick={clearAll} disabled={busy}>
            Delete all actuals
          </Button>
        </div>
      </Card>

      {parsed && (
        <Card className="overflow-x-auto p-0">
          <div className="border-b border-slate-200 px-5 py-3 text-sm font-medium text-slate-700">
            Preview — {valid.length} valid, {invalid.length} with errors
          </div>
          {parsed.length === 0 ? (
            <p className="p-5 text-sm text-slate-500">Nothing parsed. Check the format.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                  <th className="px-5 py-2 font-medium">Customer</th>
                  <th className="px-3 py-2 font-medium">Hub</th>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 text-right font-medium">Cartons</th>
                  <th className="px-3 py-2 text-right font-medium">Polybags</th>
                  <th className="px-3 py-2 text-right font-medium">Irregular</th>
                  <th className="px-3 py-2 text-right font-medium">Total</th>
                  <th className="px-5 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {parsed.slice(0, 300).map((r, i) => {
                  const dash = (n?: number) => (n == null ? "—" : fmtN(n));
                  return (
                    <tr key={i} className="border-b border-slate-100 last:border-0">
                      <td className="px-5 py-1.5 text-slate-700">{r.customer}</td>
                      <td className="px-3 py-1.5 text-slate-600">{r.hub}</td>
                      <td className="px-3 py-1.5 text-slate-600">
                        {r.isoDate ? fmtDate(r.isoDate) : r.date}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{dash(r.cartons)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{dash(r.polybags)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">{dash(r.irregular)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium text-slate-800">
                        {dash(r.vol)}
                      </td>
                      <td className="px-5 py-1.5">
                        {r.error ? (
                          <span className="text-red-600">{r.error}</span>
                        ) : (
                          <span className="text-emerald-600">ok</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {parsed.length > 300 && (
            <p className="px-5 py-2 text-xs text-slate-400">Showing first 300 of {parsed.length} rows.</p>
          )}
        </Card>
      )}
    </div>
  );
}
